import React, { useState } from "react";
import { Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Account } from "@rentablo/api/apiv1";
import { ContraAccountRow } from "@features/accounts/edit/components/AdditionalContraAccounts/ContraAccountRow";
import { SelectPrimaryPanel } from "./SelectPrimaryPanel";

interface Props {
  primary?: Account;
  candidates: Account[];
  isMutating: boolean;
  canChange: boolean;
  onChangePrimary: (id: number) => Promise<void>;
}

export const PrimaryAccountSection = ({
  primary,
  candidates,
  isMutating,
  canChange,
  onChangePrimary,
}: Props) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const canEdit = canChange && candidates.length > 0;

  const handleConfirm = async (id: number) => {
    try {
      await onChangePrimary(id);
      setIsEditing(false);
    } catch {
      // error shown via snackbar in the page
    }
  };

  return (
    <>
      <Typography
        variant="overline"
        color="textSecondary"
        display="block"
        mb={2}
      >
        {t("createAndEditAccount.additionalContraAccounts.primaryLabel")}
      </Typography>
      {primary && (
        <ContraAccountRow
          accountName={primary.name}
          iban={primary.iban}
          bankImage={primary.iconUrl}
          type={primary.typeFromBank}
          accountType={primary.type}
          to={`/accounts/${primary.id}`}
          isPrimary
          action={
            !canEdit ? undefined : isEditing ? (
              <Button
                variant="text"
                size="small"
                color="inherit"
                onClick={() => setIsEditing(false)}
                data-testid="cancel-change-primary"
              >
                {t("referenceAccountPage.cancelButton")}
              </Button>
            ) : (
              <Button
                variant="text"
                size="small"
                color="primary"
                onClick={() => setIsEditing(true)}
                data-testid="change-primary-button"
              >
                {t("referenceAccountPage.changeButton")}
              </Button>
            )
          }
        />
      )}
      {isEditing && (
        <SelectPrimaryPanel
          accounts={candidates}
          currentPrimaryId={primary?.id}
          onConfirm={handleConfirm}
          onCancel={() => setIsEditing(false)}
          isFetching={isMutating}
        />
      )}
    </>
  );
};
